import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { BrowserRouter as Router } from 'react-router-dom';
import Home from '../pages/Home';
import About from '../pages/About';
import Contact from '../pages/Contact';
import FAQ from '../pages/FAQ';
import Login from '../pages/Login';
import Register from '../pages/Register';
import Dashboard from '../pages/Dashboard';
import DailyFarmLogbookPage from '../pages/DailyFarmLogbookPage';
import SafetyPortal from '../components/SafetyPortal';
import BasicPage from '../pages/BasicPage';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ProtectedRoute from '../components/ProtectedRoute';

const AppRoutes = () => {
  return (
    <Router>
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-grow pt-16">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/daily-farm-logbook" element={<DailyFarmLogbookPage />} />
            <Route path="/portal" element={<SafetyPortal />} />
            <Route path="/login" element={<Login />} />
            <Route path="/safety-guidelines" element={
              <BasicPage 
                titleKey="safetyGuidelines.title"
                titleFallback="Safety Guidelines"
                subtitleKey="safetyGuidelines.subtitle"
                subtitleFallback="Comprehensive safety protocols for industrial hemp operations"
              >
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Standard Safety Protocols</h2>
                  <p>Our comprehensive safety guidelines ensure the protection of all personnel involved in industrial hemp cultivation and processing operations. These protocols are designed to mitigate risks associated with field operations, equipment handling, and material processing.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Personal Protective Equipment (PPE)</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Required PPE includes safety glasses, cut-resistant gloves, and steel-toed boots</li>
                    <li>Chemical-resistant suits when handling pesticides or fertilizers</li>
                    <li>Hearing protection during high-noise operations</li>
                    <li>Respiratory protection when needed for dust or chemical exposure</li>
                  </ul>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Equipment Safety</h3>
                  <p>All machinery must undergo daily safety inspections before operation. Operators must be certified and trained on all equipment used. Regular maintenance schedules must be followed to prevent mechanical failures and ensure safe operation.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Chemical Handling</h3>
                  <p>Proper storage, handling, and disposal of all chemicals used in cultivation. All safety data sheets (SDS) must be accessible. Emergency procedures for chemical exposure are clearly posted and regularly reviewed.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Emergency Procedures</h3>
                  <p>Clear evacuation routes and assembly points. First aid stations are positioned throughout all work areas. Emergency contact information is readily available. Incident reporting procedures ensure continuous improvement in safety practices.</p>
                </div>
              </BasicPage>
            } />
            <Route path="/training" element={
              <BasicPage 
                titleKey="training.title"
                titleFallback="Training Materials"
                subtitleKey="training.subtitle"
                subtitleFallback="Educational resources for safety and compliance"
              >
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Comprehensive Training Resources</h2>
                  <p>Our training materials are designed to ensure all personnel are properly educated on safety protocols, regulatory compliance, and operational procedures. Training programs are tailored to specific roles and responsibilities within the hemp operation.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Required Training Modules</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Safety protocols and emergency response procedures</li>
                    <li>Equipment operation and maintenance</li>
                    <li>Chemical handling and safety</li>
                    <li>Compliance with regulatory requirements</li>
                    <li>Quality control and documentation procedures</li>
                  </ul>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Training Delivery Methods</h3>
                  <p>We utilize multiple training delivery methods to ensure effective learning: hands-on demonstrations, e-learning modules, classroom instruction, and on-the-job training. All training is documented and competency is verified before personnel operate independently.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Ongoing Education</h3>
                  <p>Continuous education is required to maintain certifications and stay current with evolving regulations and best practices. Annual refresher training ensures knowledge retention and updates on any changes to procedures.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Assessment and Certification</h3>
                  <p>Competency assessments verify understanding of safety and operational procedures. Certifications are maintained and renewed according to regulatory requirements and internal standards.</p>
                </div>
              </BasicPage>
            } />
            <Route path="/docs" element={
              <BasicPage 
                titleKey="documentation.title"
                titleFallback="Documentation"
                subtitleKey="documentation.subtitle"
                subtitleFallback="SOPs, forms, and compliance documents"
              >
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Essential Documentation</h2>
                  <p>Our documentation system ensures regulatory compliance, operational consistency, and traceability throughout the hemp cultivation and processing cycle. All documents are maintained to meet regulatory requirements and support quality management systems.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Standard Operating Procedures (SOPs)</h3>
                  <p>Comprehensive SOPs cover all aspects of hemp operations from seed to harvest. Each procedure includes safety requirements, quality checkpoints, and compliance verification steps. Regular reviews and updates maintain current and relevant documentation.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Compliance Forms</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Daily safety checklists</li>
                    <li>Equipment inspection forms</li>
                    <li>Chemical usage logs</li>
                    <li>Personnel training records</li>
                    <li>Quality control documentation</li>
                    <li>Incident reporting forms</li>
                  </ul>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Regulatory Documentation</h3>
                  <p>All regulatory requirements are documented and maintained to ensure compliance with state, federal, and local regulations. This includes licenses, permits, and certificates required for hemp cultivation and processing.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Record Keeping</h3>
                  <p>Retention schedules ensure all required records are maintained for the required periods. Digital and physical storage systems provide secure, organized access to all documentation. Backup systems ensure availability and prevent loss of critical information.</p>
                </div>
              </BasicPage>
            } />
            <Route path="/compliance" element={
              <BasicPage 
                titleKey="compliance.title"
                titleFallback="Compliance Standards"
                subtitleKey="compliance.subtitle" 
                subtitleFallback="Regulatory requirements and standards"
              >
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Regulatory Compliance Framework</h2>
                  <p>Our comprehensive compliance program ensures adherence to all applicable regulations governing industrial hemp cultivation and processing. We maintain the highest standards of regulatory compliance to ensure legal operations and market access.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Federal Regulations</h3>
                  <p>Full compliance with the Agriculture Improvement Act of 2018 (Farm Bill) and USDA regulations for hemp production. This includes THC concentration limits, licensing requirements, and production plan submissions to state departments of agriculture.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">State and Local Compliance</h3>
                  <p>Adherence to all state-specific regulations and requirements, including licensing, testing, and reporting. Local zoning and environmental regulations are also strictly followed in all operation areas.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Quality Standards</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>THC testing and certification protocols</li>
                    <li>Pesticide and contaminant testing requirements</li>
                    <li>Organic certification compliance (when applicable)</li>
                    <li>Good Manufacturing Practices (GMP) for processing</li>
                    <li>Traceability and record-keeping standards</li>
                  </ul>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Audit and Inspection Readiness</h3>
                  <p>Regular internal audits ensure continuous compliance. Documentation and procedures are maintained to ensure successful completion of regulatory inspections and audits. Corrective action procedures address any compliance issues immediately.</p>
                </div>
              </BasicPage>
            } />
            <Route path="/privacy-policy" element={
              <BasicPage 
                titleKey="privacy.title"
                titleFallback="Privacy Policy"
                subtitleKey="privacy.subtitle"
                subtitleFallback="How we collect, use, and protect your information"
              >
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Information Privacy and Protection</h2>
                  <p>This Privacy Policy describes how we collect, use, and protect your personal information when you use our Industrial Hemp Safety & Compliance Portal. We are committed to safeguarding your privacy and maintaining the highest standards of data protection.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Information We Collect</h3>
                  <p>We collect information you provide directly to us, including when you register for our services, complete forms, or communicate with us. This may include your name, contact information, and other relevant operational data necessary for our safety and compliance services.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">How We Use Your Information</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>To provide and maintain our safety management services</li>
                    <li>To process and document safety and compliance data</li>
                    <li>To communicate with you about your account and services</li>
                    <li>To comply with our legal obligations</li>
                    <li>To improve our services and user experience</li>
                  </ul>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Data Security</h3>
                  <p>We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. Our security measures include encryption, access controls, and regular security assessments.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Your Rights</h3>
                  <p>You have the right to access, update, or delete your personal information. You may also object to or request restriction of processing, and have the right to data portability. Contact us to exercise these rights or with any privacy concerns.</p>
                </div>
              </BasicPage>
            } />
            <Route path="/terms" element={
              <BasicPage 
                titleKey="terms.title"
                titleFallback="Terms of Service"
                subtitleKey="terms.subtitle"
                subtitleFallback="Terms and conditions for using our services"
              >
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Terms of Service</h2>
                  <p>These Terms of Service govern your access to and use of the Industrial Hemp Safety & Compliance Portal. By using our services, you accept and agree to be bound by these terms and conditions.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Service Description</h3>
                  <p>Our portal provides comprehensive safety management and compliance tools for industrial hemp cultivation and processing operations. This includes form submission, data tracking, reporting capabilities, and compliance documentation tools.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">User Responsibilities</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Provide accurate and complete information when required</li>
                    <li>Maintain the confidentiality of your account credentials</li>
                    <li>Use the services in compliance with applicable laws and regulations</li>
                    <li>Not misuse or interfere with the service operations</li>
                    <li>Comply with all posted policies and guidelines</li>
                  </ul>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Limitation of Liability</h3>
                  <p>To the maximum extent permitted by law, we shall not be liable for any indirect, special, incidental, or consequential damages arising from your use of our services, even if we have been advised of the possibility of such damages.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Changes to Terms</h3>
                  <p>We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting. Your continued use of the services following any changes constitutes acceptance of the new terms.</p>
                </div>
              </BasicPage>
            } />
            <Route path="/cookie-policy" element={
              <BasicPage 
                titleKey="cookies.title"
                titleFallback="Cookie Policy"
                subtitleKey="cookies.subtitle"
                subtitleFallback="Information about cookies we use"
              >
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Cookie Policy</h2>
                  <p>This Cookie Policy explains how we use cookies and similar tracking technologies to provide and improve our Industrial Hemp Safety & Compliance Portal.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">What Are Cookies?</h3>
                  <p>Cookies are small text files that are stored on your computer or mobile device when you visit our website. They help us distinguish you from other users and improve your experience when using our services.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Types of Cookies We Use</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Essential Cookies</strong>: These cookies are necessary to provide our services and cannot be turned off.</li>
                    <li><strong>Performance Cookies</strong>: These cookies help us improve our services by collecting information about how visitors use our site.</li>
                    <li><strong>Functionality Cookies</strong>: These cookies allow us to remember your choices and provide enhanced features.</li>
                    <li><strong>Targeting Cookies</strong>: These cookies may be used to deliver content that is more relevant to you.</li>
                  </ul>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">How to Control Cookies</h3>
                  <p>Most web browsers allow you to control cookies through their settings preferences. However, restricting cookies may impact your experience of our services. You can set your browser to block or alert you to certain cookies, though this may affect site functionality.</p>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Updates to This Policy</h3>
                  <p>We may update this Cookie Policy from time to time. You should check this page regularly to review any changes. Your continued use of our services constitutes acceptance of such changes.</p>
                </div>
              </BasicPage>
            } />
            <Route path="/register" element={<Register />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
};

export default AppRoutes;